import { useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import { useLanguage } from "@/hooks/use-language";
import { useGroupMembers } from "@/hooks/use-groups";
import { NotificationPopover } from "@/components/notification-popover";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from "@/components/ui/sheet";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Menu,
  BookOpen,
  ClipboardList,
  BarChart3,
  User,
  LogOut,
  BrainCircuit,
  Home,
  Play,
  UserCog,
  ArrowLeft,
  Settings,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface NavLink {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

// 시험 메뉴 5개를 prefix별로 만든다. 그룹 시험은 prefix가 /groups/$gid,
// 개인 시험은 빈 문자열이라 같은 hrefs가 그대로 유효.
function buildExamLinks(
  prefix: string,
  slug: string,
  t: (k: string) => string,
): NavLink[] {
  const base = `${prefix}/exams/${slug}`;
  return [
    { href: base, label: t("nav.dashboard"), icon: Home },
    { href: `${base}/questions`, label: t("nav.question_bank"), icon: BookOpen },
    { href: `${base}/test/new`, label: t("nav.start_test"), icon: Play },
    { href: `${base}/history`, label: t("nav.test_history"), icon: ClipboardList },
    { href: `${base}/analytics`, label: t("nav.analytics"), icon: BarChart3 },
  ];
}

export function Navbar() {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const pathname = useLocation({ select: (s) => s.pathname });

  const handleLogout = () => {
    logout();
    navigate({ to: "/" });
  };
  const [menuOpen, setMenuOpen] = useState(false);

  // path만으로 세 가지 컨텍스트를 분기:
  //  • groupExam   = /groups/{gid}/exams/{slug}/...  (그룹 시험 풀이)
  //  • exam        = /exams/{slug}/...                (개인 시험)
  //  • groupConsole = /groups/{gid}                   (그룹 콘솔)
  const groupExamMatch = pathname.match(
    /^\/groups\/([^/]+)\/exams\/([^/]+)/,
  );
  const examMatch = !groupExamMatch
    ? pathname.match(/^\/exams\/([^/]+)/)
    : null;
  const groupConsoleMatch = !groupExamMatch
    ? pathname.match(/^\/groups\/([^/]+)$/)
    : null;

  const groupCtx = groupExamMatch
    ? { gid: groupExamMatch[1], slug: groupExamMatch[2] }
    : null;
  const examSlug = examMatch?.[1];
  const groupConsoleId = groupConsoleMatch ? groupConsoleMatch[1] : null;
  const isAdmin = user?.role === "admin";

  // 그룹 콘솔에서 [홈, 관리] 메뉴를 GNB로 노출. 관리는 site-admin이거나
  // 해당 그룹의 owner/admin인 경우에만. 콘솔이 아닐 때는 hook이 비활성
  // (gid="")이라 추가 fetch 없음.
  const search = useSearch({ strict: false }) as { tab?: string };
  const { data: members = [] } = useGroupMembers(groupConsoleId ?? "");
  const myGroupRole = groupConsoleId
    ? members.find((m) => m.user_id === user?.id)?.role
    : undefined;
  const canManageGroup =
    !!groupConsoleId &&
    (isAdmin || myGroupRole === "owner" || myGroupRole === "admin");

  // 시험 컨텍스트일 때만 시험 메뉴 5개를 노출. 그룹 콘솔에서는 [홈, 관리]
  // 두 탭을 GNB로. 그 외 페이지는 가로 메뉴 비움.
  const navLinks: NavLink[] = groupCtx
    ? buildExamLinks(`/groups/${groupCtx.gid}`, groupCtx.slug, t)
    : examSlug
      ? buildExamLinks("", examSlug, t)
      : groupConsoleId
        ? [
            {
              href: `/groups/${groupConsoleId}?tab=home`,
              label: "홈",
              icon: Home,
            },
            ...(canManageGroup
              ? [
                  {
                    href: `/groups/${groupConsoleId}?tab=manage`,
                    label: "관리",
                    icon: Settings,
                  },
                ]
              : []),
          ]
        : [];

  // 백 버튼 분기: 그룹 시험 컨텍스트면 "← 그룹 대시보드", 그 외 inner
  // 페이지면 "← 전체 시험". 두 버튼이 동시에 보이지 않게 한 단계만.
  const isDashboardPath =
    pathname === "/dashboard" || pathname === "/" || pathname === "";
  const showGroupBack = !!groupCtx;
  const showDashboardBack = !isDashboardPath && !groupCtx;

  const groupTab = search?.tab ?? "home";
  const isActive = (href: string) => {
    // 그룹 콘솔은 path가 동일하고 ?tab=만 다르므로 search param으로 비교.
    if (groupConsoleId) {
      const q = href.split("?")[1] ?? "";
      const tab = new URLSearchParams(q).get("tab") ?? "home";
      return pathname === `/groups/${groupConsoleId}` && tab === groupTab;
    }
    if (pathname === href) return true;
    const isExamRoot =
      groupCtx
        ? href === `/groups/${groupCtx.gid}/exams/${groupCtx.slug}`
        : examSlug
          ? href === `/exams/${examSlug}`
          : false;
    if (isExamRoot) return false;
    return pathname.startsWith(href + "/");
  };

  const userInitial = user?.email?.charAt(0).toUpperCase() ?? "U";

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/80 backdrop-blur-md supports-backdrop-filter:bg-background/60">
      <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4 sm:px-6">
        <Link
          {...(groupCtx
            ? {
                to: "/groups/$gid/exams/$slug",
                params: { gid: groupCtx.gid, slug: groupCtx.slug },
              }
            : examSlug
              ? { to: "/exams/$slug", params: { slug: examSlug } }
              : { to: "/dashboard" })}
          className="flex items-center gap-2 font-semibold tracking-tight"
        >
          <BrainCircuit className="size-5 text-primary" />
          <span className="text-sm sm:text-base">CBEET</span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {showGroupBack && groupCtx && (
            <Link
              to="/groups/$gid"
              params={{ gid: groupCtx.gid }}
              search={{ tab: "home" }}
            >
              <Button
                variant="ghost"
                size="sm"
                className="cursor-pointer gap-1.5 text-xs text-muted-foreground"
              >
                <ArrowLeft className="size-3.5" />
                그룹 대시보드
              </Button>
            </Link>
          )}
          {showDashboardBack && (
            <Link to="/dashboard">
              <Button
                variant="ghost"
                size="sm"
                className="cursor-pointer gap-1.5 text-xs text-muted-foreground"
              >
                <ArrowLeft className="size-3.5" />
                {t("nav.all_exams")}
              </Button>
            </Link>
          )}
          {navLinks.map((link) => (
            <Link key={link.href} to={link.href}>
              <Button
                variant={isActive(link.href) ? "secondary" : "ghost"}
                size="sm"
                className={cn(
                  "cursor-pointer gap-1.5 text-sm",
                  isActive(link.href) ? "font-medium" : "text-muted-foreground",
                )}
              >
                <link.icon className="size-4" />
                {link.label}
              </Button>
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-1">
          {isAuthenticated && <NotificationPopover />}
          {isAuthenticated ? (
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger
                aria-label={t("nav.menu")}
                className="cursor-pointer rounded-md p-2 transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <Menu className="size-5" />
                <span className="sr-only">{t("nav.open_menu")}</span>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-3">
                    <Avatar size="sm">
                      <AvatarFallback>{userInitial}</AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col items-start text-left">
                      <span className="text-sm font-medium">{user?.email}</span>
                      {isAdmin && (
                        <span className="text-xs text-muted-foreground">
                          {t("nav.admin_role")}
                        </span>
                      )}
                    </div>
                  </SheetTitle>
                </SheetHeader>

                <nav className="flex flex-col gap-1 px-4">
                  {/* 모바일에선 GNB 가로 nav가 안 보이니 sheet 안에 컨텍스트 nav도 함께 노출 */}
                  <div className="flex flex-col gap-1 md:hidden">
                    {showGroupBack && groupCtx && (
                      <SheetClose render={<span />}>
                        <Link
                          to="/groups/$gid"
                          params={{ gid: groupCtx.gid }}
                          search={{ tab: "home" }}
                          onClick={() => setMenuOpen(false)}
                          className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        >
                          <ArrowLeft className="size-5" />
                          그룹 대시보드
                        </Link>
                      </SheetClose>
                    )}
                    {showDashboardBack && (
                      <SheetClose render={<span />}>
                        <Link
                          to="/dashboard"
                          onClick={() => setMenuOpen(false)}
                          className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        >
                          <Home className="size-5" />
                          {t("nav.all_exams")}
                        </Link>
                      </SheetClose>
                    )}
                    {navLinks.map((link) => (
                      <SheetClose key={link.href} render={<span />}>
                        <Link
                          to={link.href}
                          onClick={() => setMenuOpen(false)}
                          className={cn(
                            "flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-colors",
                            isActive(link.href)
                              ? "bg-secondary text-foreground"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground",
                          )}
                        >
                          <link.icon className="size-5" />
                          {link.label}
                        </Link>
                      </SheetClose>
                    ))}
                    {(showGroupBack || showDashboardBack) && (
                      <div className="my-2 h-px bg-border" aria-hidden />
                    )}
                  </div>

                  <SheetClose render={<span />}>
                    <Link
                      to="/profile"
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    >
                      <User className="size-5" />
                      {t("nav.profile")}
                    </Link>
                  </SheetClose>
                  {isAdmin && (
                    <SheetClose render={<span />}>
                      <Link
                        to="/admin"
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      >
                        <UserCog className="size-5" />
                        {t("nav.admin_console")}
                      </Link>
                    </SheetClose>
                  )}
                  <button
                    onClick={() => {
                      handleLogout();
                      setMenuOpen(false);
                    }}
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    <LogOut className="size-5" />
                    {t("nav.logout")}
                  </button>
                </nav>
              </SheetContent>
            </Sheet>
          ) : (
            <Link to="/register">
              <Button size="sm" className="cursor-pointer">
                {t("nav.register")}
              </Button>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
